import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/lib/currencyCtx";
import { fmt } from "@/lib/currency";
import { Link } from "react-router-dom";
import { Plus, Trash2, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";

export default function AdminDistributions() {
  const { currency, rate } = useCurrency();
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [title, setTitle] = useState("");
  const [netProfit, setNetProfit] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data } = await api.get("/distributions");
    setList(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const createNew = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/distributions", {
        year: Number(year),
        title: title || `FY ${year}`,
        net_profit: Number(netProfit),
        entries: [],
      });
      toast.success("Distribution created");
      setShow(false);
      setTitle(""); setNetProfit(0);
      window.location.href = `/admin/distributions/${data.distribution_id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this distribution?")) return;
    await api.delete(`/distributions/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="anim-fade-up">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="eyebrow mb-2">Annual</div>
          <h1 className="display-serif text-5xl text-atf-brand">Distributions</h1>
          <p className="text-atf-text2 mt-3 max-w-xl">Each fiscal year, run the engine, publish the ledger.</p>
        </div>
        <button onClick={() => setShow(true)} data-testid="new-distribution-button" className="bg-atf-brand text-white px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold hover:bg-atf-brand2 flex items-center gap-2">
          <Plus size={14} /> New Distribution
        </button>
      </div>

      <div className="border border-atf-border bg-white">
        <table className="w-full text-sm" data-testid="distributions-table">
          <thead><tr className="border-b border-atf-border">
            <Th>Year</Th><Th>Title</Th><Th align="right">Net Profit</Th><Th align="right">Pool</Th><Th>Phase</Th><Th>Status</Th><Th align="right">Actions</Th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={7} className="p-6 text-center text-atf-text2">Loading…</td></tr>}
            {!loading && list.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center">
                <div className="font-serif text-xl text-atf-brand mb-2">No distributions yet.</div>
                <div className="text-atf-text2">Create one to enter the year’s net profit and per-partner figures.</div>
              </td></tr>
            )}
            {list.map((d) => (
              <tr key={d.distribution_id} className="border-b border-atf-border last:border-b-0 hover:bg-atf-subtle/50" data-testid={`dist-row-${d.distribution_id}`}>
                <Td><span className="font-serif text-xl text-atf-brand">{d.year}</span></Td>
                <Td>{d.title}</Td>
                <Td align="right" mono>{fmt(d.net_profit, currency, rate)}</Td>
                <Td align="right" mono>{fmt(d.distributable_pool, currency, rate)}</Td>
                <Td><span className="text-xs text-atf-text2">{d.phase_name}</span></Td>
                <Td>
                  {d.status === "published" ? (
                    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold text-atf-positive"><CheckCircle2 size={12} /> Published</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider font-semibold text-atf-text2"><Circle size={12} /> Draft</span>
                  )}
                </Td>
                <Td align="right">
                  <div className="flex items-center justify-end gap-3">
                    <Link to={`/admin/distributions/${d.distribution_id}`} data-testid={`open-${d.distribution_id}`} className="text-xs uppercase tracking-[0.18em] font-semibold text-atf-brand border-b border-atf-brand pb-0.5">Open</Link>
                    <button onClick={() => remove(d.distribution_id)} data-testid={`del-${d.distribution_id}`} className="text-atf-text2 hover:text-atf-negative"><Trash2 size={14} /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={() => setShow(false)}>
          <div className="bg-white border border-atf-border max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="new-dist-modal">
            <div className="px-8 py-6 border-b border-atf-border">
              <div className="eyebrow mb-1">Begin a new distribution</div>
              <h2 className="font-serif text-2xl text-atf-brand">Fiscal year setup</h2>
            </div>
            <form onSubmit={createNew} className="p-8 space-y-5">
              <Field label="Year"><input data-testid="new-year" required type="number" value={year} onChange={(e) => setYear(e.target.value)} className={INPUT} /></Field>
              <Field label="Title (optional)"><input data-testid="new-title" value={title} onChange={(e) => setTitle(e.target.value)} className={INPUT} placeholder={`FY ${year}`} /></Field>
              <Field label="Net Profit (₹)"><input data-testid="new-netprofit" required type="number" min={0} value={netProfit} onChange={(e) => setNetProfit(e.target.value)} className={INPUT} /></Field>
              <div className="flex justify-end gap-3 pt-4 border-t border-atf-border">
                <button type="button" onClick={() => setShow(false)} className="px-5 py-2.5 border border-atf-border text-xs uppercase tracking-[0.18em] font-semibold hover:bg-atf-subtle">Cancel</button>
                <button type="submit" data-testid="create-distribution" className="px-6 py-2.5 bg-atf-brand text-white text-xs uppercase tracking-[0.18em] font-semibold hover:bg-atf-brand2">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const Th = ({ children, align = "left" }) => <th className={`px-4 py-3 eyebrow text-${align}`}>{children}</th>;
const Td = ({ children, align = "left", mono }) => <td className={`px-4 py-3 text-${align} ${mono ? "tabular" : ""}`}>{children}</td>;
const Field = ({ label, children }) => (
  <div><label className="eyebrow block mb-2">{label}</label>{children}</div>
);
const INPUT = "w-full border border-atf-border bg-white px-3 py-2.5 text-sm focus:outline-none focus:border-atf-brand focus:ring-1 focus:ring-atf-brand";
